/*
  Kalessie - custom PDP gallery behaviour.
  Thumb / dot click + horizontal swipe change the active slide; tapping the play
  overlay on a video slide swaps in the deferred <video>/<iframe>. On variant
  change, shows the selected variant's featured media (matched by media id).
  Loaded after pubsub.js/constants.js so `subscribe` + PUB_SUB_EVENTS exist.
*/
(function () {
  if (window.customElements.get('kal-gallery')) return;

  class KalGallery extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('.kal-gallery__slide'));
      this.thumbs = Array.from(this.querySelectorAll('.kal-gallery__thumb'));
      this.dots = Array.from(this.querySelectorAll('.kal-gallery__dot'));
      this.track = this.querySelector('.kal-gallery__track');
      this.active = parseInt(this.dataset.active || '0', 10) || 0;

      this.thumbs.forEach((t) => t.addEventListener('click', () => this.go(parseInt(t.dataset.go, 10))));
      this.dots.forEach((d) => d.addEventListener('click', () => this.go(parseInt(d.dataset.go, 10))));

      this.querySelectorAll('[data-kal-video] .kal-gallery__play').forEach((btn) => {
        btn.addEventListener('click', (e) => { e.stopPropagation(); this.playVideo(btn.closest('[data-kal-video]')); });
      });

      this.bindSwipe();
      this.bindVariant();
      this.bindWish();
      this.bindZoom();
    }

    disconnectedCallback() {
      if (typeof this._unsub === 'function') this._unsub();
      if (this._onKey) document.removeEventListener('keydown', this._onKey);
      if (this.lightbox) this.lightbox.remove();
    }

    go(index) {
      if (isNaN(index) || index < 0 || index >= this.slides.length) return;
      this.active = index;
      this.slides.forEach((s, i) => s.classList.toggle('is-active', i === index));
      this.thumbs.forEach((t) => t.classList.toggle('is-active', parseInt(t.dataset.go, 10) === index));
      // The rail is capped to the stage height and scrolls when a product has
      // more media than fits; keep the active thumb in view.
      const activeThumb = this.thumbs[index];
      const rail = activeThumb && activeThumb.parentElement;
      if (rail && rail.scrollHeight > rail.clientHeight) {
        const t = activeThumb.offsetTop;
        const h = activeThumb.offsetHeight;
        if (t < rail.scrollTop) rail.scrollTo({ top: t - 4, behavior: 'smooth' });
        else if (t + h > rail.scrollTop + rail.clientHeight) {
          rail.scrollTo({ top: t + h - rail.clientHeight + 4, behavior: 'smooth' });
        }
      }
      this.dots.forEach((d) => d.classList.toggle('is-active', parseInt(d.dataset.go, 10) === index));
    }

    goByMediaId(id) {
      var slide = this.slides.find(function (s) { return String(s.dataset.mediaId) === String(id); });
      if (slide) this.go(parseInt(slide.dataset.slideIndex, 10));
    }

    playVideo(host) {
      if (!host || host.dataset.kalPlaying) return;
      var tpl = host.querySelector('template');
      if (!tpl) return;
      host.dataset.kalPlaying = 'true';
      host.appendChild(tpl.content.cloneNode(true));
      host.classList.add('is-playing');
      var media = host.querySelector('video, iframe');
      if (media && media.tagName === 'VIDEO' && typeof media.play === 'function') media.play().catch(function () {});
    }

    bindSwipe() {
      if (!this.track) return;
      var startX = 0;
      var self = this;
      this.track.addEventListener('touchstart', function (e) { startX = e.touches[0].clientX; }, { passive: true });
      this.track.addEventListener('touchend', function (e) {
        var dx = e.changedTouches[0].clientX - startX;
        if (Math.abs(dx) < 40) return;
        self.go(dx < 0 ? self.active + 1 : self.active - 1);
      }, { passive: true });
    }

    bindVariant() {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
        this._unsub = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          var media = event && event.data && event.data.variant && event.data.variant.featured_media;
          if (media && media.id) this.goByMediaId(media.id);
        });
      }
    }

    bindWish() {
      var wish = this.querySelector('.kal-gallery__wish');
      if (!wish) return;
      wish.addEventListener('click', function () {
        var on = wish.getAttribute('aria-pressed') === 'true';
        wish.setAttribute('aria-pressed', String(!on));
        wish.textContent = on ? '♡' : '♥';
      });
    }

    // ── Full-screen slideshow ────────────────────────────────────────
    // Only photo slides open it; a video slide keeps its play overlay.
    bindZoom() {
      this.zoomable = this.slides
        .map((slide, i) => {
          if (!slide.dataset.zoom) return null;
          var img = slide.querySelector('.kal-gallery__img');
          if (!img) return null;
          return { index: i, full: slide.dataset.zoom, thumb: img.currentSrc || img.src, alt: img.alt || '' };
        })
        .filter(Boolean);
      if (!this.zoomable.length) return;

      this.querySelectorAll('.kal-gallery__slide[data-zoom] .kal-gallery__img').forEach((img) => {
        img.addEventListener('click', () => this.openZoom(this.active));
      });
    }

    buildLightbox() {
      if (this.lightbox) return this.lightbox;
      var lb = document.createElement('div');
      lb.className = 'kal-lightbox';
      lb.hidden = true;
      lb.innerHTML =
        '<div class="kal-lightbox__backdrop" data-lb-close></div>' +
        '<div class="kal-lightbox__stage">' +
        '<span class="kal-lightbox__count" data-lb-count></span>' +
        '<button type="button" class="kal-lightbox__close" data-lb-close aria-label="Close">&times;</button>' +
        '<button type="button" class="kal-lightbox__nav kal-lightbox__nav--prev" data-lb-prev aria-label="Previous image">&#8249;</button>' +
        '<img class="kal-lightbox__img" data-lb-img alt="">' +
        '<button type="button" class="kal-lightbox__nav kal-lightbox__nav--next" data-lb-next aria-label="Next image">&#8250;</button>' +
        '</div>' +
        '<div class="kal-lightbox__rail" data-lb-rail></div>';

      var rail = lb.querySelector('[data-lb-rail]');
      this.zoomable.forEach((item, n) => {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'kal-lightbox__thumb';
        b.setAttribute('aria-label', 'View image ' + (n + 1));
        b.innerHTML = '<img src="' + item.thumb + '" alt="">';
        b.addEventListener('click', () => this.showZoom(n));
        rail.appendChild(b);
      });

      lb.addEventListener('click', (e) => { if (e.target.closest('[data-lb-close]')) this.closeZoom(); });
      lb.querySelector('[data-lb-prev]').addEventListener('click', () => this.showZoom(this.zoomIndex - 1));
      lb.querySelector('[data-lb-next]').addEventListener('click', () => this.showZoom(this.zoomIndex + 1));

      var startX = 0;
      lb.addEventListener('touchstart', (e) => { startX = e.touches[0].clientX; }, { passive: true });
      lb.addEventListener('touchend', (e) => {
        var dx = e.changedTouches[0].clientX - startX;
        if (Math.abs(dx) < 40) return;
        this.showZoom(this.zoomIndex + (dx < 0 ? 1 : -1));
      }, { passive: true });

      this._onKey = (e) => {
        if (lb.hidden) return;
        if (e.key === 'Escape') this.closeZoom();
        else if (e.key === 'ArrowRight') this.showZoom(this.zoomIndex + 1);
        else if (e.key === 'ArrowLeft') this.showZoom(this.zoomIndex - 1);
      };
      document.addEventListener('keydown', this._onKey);

      // Dawn's scroll-trigger animation leaves a transform on the product
      // wrappers, which would capture fixed positioning — keep it on <body>.
      document.body.appendChild(lb);
      this.lightbox = lb;
      return lb;
    }

    openZoom(slideIndex) {
      var pos = this.zoomable.findIndex((z) => z.index === slideIndex);
      var lb = this.buildLightbox();
      this._lastFocus = document.activeElement;
      lb.hidden = false;
      document.body.classList.add('kal-lightbox-locked');
      this.showZoom(pos < 0 ? 0 : pos);
      lb.querySelector('.kal-lightbox__close').focus();
    }

    showZoom(n) {
      var total = this.zoomable.length;
      if (n < 0) n = total - 1;
      if (n >= total) n = 0;
      this.zoomIndex = n;
      var lb = this.lightbox;
      var item = this.zoomable[n];
      var img = lb.querySelector('[data-lb-img]');
      img.src = item.full;
      img.alt = item.alt;
      lb.querySelector('[data-lb-count]').textContent = n + 1 + ' / ' + total;
      lb.querySelectorAll('.kal-lightbox__thumb').forEach((t, i) => t.classList.toggle('is-active', i === n));
      // Keep the gallery behind the overlay on the same image.
      this.go(item.index);
    }

    closeZoom() {
      if (!this.lightbox) return;
      this.lightbox.hidden = true;
      document.body.classList.remove('kal-lightbox-locked');
      if (this._lastFocus && typeof this._lastFocus.focus === 'function') this._lastFocus.focus();
    }
  }

  window.customElements.define('kal-gallery', KalGallery);
})();
